/**
 * columnPickerGroups — maps the pure `ColumnGroup[]` (from `columnsToGroups`)
 * into the JSX-bearing `CommandPickerGroup[]` the shared `CommandPickerPopover`
 * renders: each item gets the column name (or its qualified `alias.col` form),
 * a muted data-type, and PK/FK badges. Shared by the SELECT "add column",
 * WHERE "add filter" column step, and the JOIN ON-column pickers so they all
 * look and search the same.
 *
 * `selectedKeys` (SELECT / ORDER BY / GROUP BY adders) check-marks the columns
 * already in the clause and turns the group's bulk action into "+ N remaining"
 * (hidden once every column of the table is in).
 */

import { ColumnBadges } from './ColumnBadges';
import { columnGroupTitle, type ColumnGroup } from './columnGroups';
import type { CommandPickerGroup } from './CommandPickerPopover';

export function toCommandPickerGroups(
  groups: readonly ColumnGroup[],
  opts: {
    readonly qualified?: boolean;
    readonly withAddAll?: boolean;
    readonly selectedKeys?: ReadonlySet<string>;
  } = {},
): CommandPickerGroup[] {
  const selected = opts.selectedKeys;
  return groups.map((group) => {
    const title = columnGroupTitle(group);
    const remaining = selected
      ? group.items.filter((item) => !selected.has(item.key)).length
      : group.items.length;
    const action =
      opts.withAddAll && remaining > 0
        ? {
            // Key by the folded relation so the consumer can look the group's
            // columns back up to add them.
            key: group.relationFolded,
            ...(remaining === group.items.length
              ? {
                  label: `+ all ${remaining}`,
                  tooltip: `Add all ${remaining} columns from ${title}`,
                }
              : {
                  label: `+ ${remaining} remaining`,
                  tooltip: `Add the ${remaining} columns from ${title} that are not in the list yet`,
                }),
          }
        : undefined;
    return {
      heading: title,
      ...(action ? { action } : {}),
      items: group.items.map((item) => ({
        key: item.key,
        value: item.searchValue,
        label: opts.qualified ? item.qualifiedLabel : item.columnName,
        meta: item.dataType,
        badges: <ColumnBadges isPrimaryKey={item.isPrimaryKey} isForeignKey={item.isForeignKey} />,
        ...(selected ? { checked: selected.has(item.key) } : {}),
      })),
    };
  });
}
